import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  allowCustom = true,
  className,
  disabled = false,
  required = false,
  name,
  filterMode = 'includes', // 'includes' or 'startsWith'
  // Props for saving custom cities
  saveCustomCity = false,
  country = '',
  state = '',
  onCustomCitySaved = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [savingCity, setSavingCity] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term and filter mode
  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLowerCase();
    const optionLower = option.toLowerCase();
    
    if (filterMode === 'startsWith') {
      // For nukh fields: match if option starts with search OR 
      // if the English name in brackets starts with search
      if (optionLower.startsWith(searchLower)) return true;
      
      // Extract English name from brackets if present (e.g., "बगरीया (Bagariya)" -> "bagariya")
      const bracketMatch = option.match(/\(([^)]+)\)/);
      if (bracketMatch) {
        const englishName = bracketMatch[1].toLowerCase();
        if (englishName.startsWith(searchLower)) return true;
      }
      return false;
    }
    
    return optionLower.includes(searchLower);
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle option selection
  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    setCustomValue('');
  };

  // Handle custom value entry
  const handleCustomSubmit = async () => {
    if (customValue.trim()) {
      const cityName = customValue.trim();
      
      // If saveCustomCity is enabled, save to backend
      if (saveCustomCity && country) {
        setSavingCity(true);
        try {
          const response = await fetch(`${BACKEND_URL}/api/custom-cities`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              country: country,
              state: state,
              city: cityName
            })
          });
          
          if (response.ok) {
            // Notify parent component that a custom city was saved (with location context)
            if (onCustomCitySaved) {
              onCustomCitySaved(cityName, country, state);
            }
          }
        } catch (error) {
          console.error('Failed to save custom city:', error);
        } finally {
          setSavingCity(false);
        }
      }
      
      onChange(cityName);
      setIsOpen(false);
      setSearchTerm('');
      setCustomValue('');
    }
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setCustomValue('');
  };

  // Check if current value is custom (not in options)
  const isCustomValue = value && !options.includes(value);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOpen && 'ring-2 ring-ring ring-offset-2',
          className
        )}
      >
        <span className={cn('truncate', !value && 'text-muted-foreground')}>
          {value || placeholder}
        </span>
        <div className="flex items-center gap-1">
          {value && !disabled && (
            <X
              className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        </div>
      </button>

      {/* Hidden input for form submission */}
      <input type="hidden" name={name} value={value || ''} required={required} />

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleSelect(option)}
                  className={cn(
                    'px-3 py-2 text-sm cursor-pointer rounded-sm',
                    'hover:bg-accent hover:text-accent-foreground',
                    value === option && 'bg-accent text-accent-foreground'
                  )}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No options found
              </div>
            )}
          </div>

          {/* Custom Value Input */}
          {allowCustom && (
            <div className="p-2 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter other city..."
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !savingCity && handleCustomSubmit()}
                  className="flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
                  disabled={savingCity}
                />
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  disabled={!customValue.trim() || savingCity}
                  className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCity ? 'Saving...' : 'Add'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {saveCustomCity 
                  ? "Can't find your city? Add it here and it will be available for others too."
                  : "Can't find your city? Type it above and click Add."
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
